import { useEffect, useRef, useState } from "react";
import { Download, Images, Pencil, Plus, Star, Trash2, Upload } from "lucide-react";
import api, { authHeaders, downloadCsv } from "@/lib/api";
import { imgSrc, PROJECT_CATEGORIES } from "@/lib/constants";

const EMPTY = {
  title: "", category: PROJECT_CATEGORIES[0], description: "", images: [],
  cover: "", featured: false, published: false, order: 0,
};

export default function ProjectsTab() {
  const [projects, setProjects] = useState(null);
  const [categories, setCategories] = useState(PROJECT_CATEGORIES);
  const [editing, setEditing] = useState(false);
  const [id, setId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/projects", { headers: authHeaders() }).then(({ data }) => setProjects(data || []));
    api.get("/config").then(({ data }) => {
      if (data.project_categories?.length) setCategories(data.project_categories);
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setUploadErr("");
    const urls = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...urls], cover: f.cover || urls[0] }));
    } catch (e) {
      setUploadErr(e.response?.data?.detail || "Încărcarea a eșuat. Încearcă din nou.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    setForm((f) => ({ ...f, images: [...f.images, url], cover: f.cover || url }));
    setUrlInput("");
  };

  const removeImage = (idx) => {
    const images = form.images.filter((_, i) => i !== idx);
    setForm({ ...form, images, cover: form.cover === form.images[idx] ? (images[0] || "") : form.cover });
  };

  const openNew = () => {
    setId(null);
    setForm({ ...EMPTY, category: categories[0] });
    setEditing(true);
  };

  const openEdit = (p) => {
    setId(p._id);
    setForm({
      title: p.title || "", category: p.category || categories[0], description: p.description || "",
      images: p.images || [], cover: p.cover || p.images?.[0] || "",
      featured: Boolean(p.featured), published: Boolean(p.published), order: p.order || 0,
    });
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title, slug: "", category: form.category, description: form.description,
      images: form.images, cover: form.cover || form.images[0] || "",
      featured: form.featured, published: form.published, order: Number(form.order) || 0,
      location: "", work_type: form.category, area: "", duration: "",
      before_images: [], during_images: [], after_images: [], services: [],
    };
    if (id) {
      await api.put(`/admin/projects/${id}`, payload, { headers: authHeaders() });
    } else {
      await api.post("/admin/projects", payload, { headers: authHeaders() });
    }
    setEditing(false);
    api.get("/admin/projects", { headers: authHeaders() }).then(({ data }) => setProjects(data || []));
  };

  const remove = (pid) => {
    if (!window.confirm("Ștergi definitiv acest proiect?")) return;
    api.delete(`/admin/projects/${pid}`, { headers: authHeaders() }).then(() =>
      api.get("/admin/projects", { headers: authHeaders() }).then(({ data }) => setProjects(data || []))
    );
  };

  const sorted = (projects || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="admin-projects-tab">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="display-title text-2xl">Proiecte ({projects?.length ?? "..."})</h2>
        <button onClick={openNew} className="btn-primary !px-5 !py-2.5 !min-h-[44px] text-base" data-testid="admin-project-new-btn">
          <Plus className="h-4 w-4" />
          PROIECT NOU
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="mb-8 space-y-5 border border-copper/40 bg-ink-900 p-6" data-testid="admin-project-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label-brox">Titlu (opțional — implicit se folosește categoria)</label>
              <input className="input-brox" value={form.title} onChange={set("title")} data-testid="admin-project-title-input" />
            </div>
            <div>
              <label className="label-brox">Categorie *</label>
              <select className="input-brox" value={form.category} onChange={set("category")} data-testid="admin-project-category-select">
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label-brox">Descriere (opțional)</label>
            <textarea className="input-brox min-h-[80px] resize-none" value={form.description} onChange={set("description")} data-testid="admin-project-description-input" />
          </div>

          <div>
            <label className="label-brox">Fotografii proiect</label>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex min-h-[48px] cursor-pointer items-center gap-2 border border-copper/50 bg-copper/10 px-5 font-display text-base font-bold uppercase tracking-wider text-copper transition-colors hover:bg-copper hover:text-ink-950">
                <Upload className="h-4 w-4" />
                {uploading ? "SE ÎNCARCĂ..." : "ÎNCARCĂ POZE"}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  className="hidden"
                  onChange={(e) => uploadFiles(e.target.files)}
                  data-testid="admin-project-upload-input"
                />
              </label>
              <div className="flex flex-1 gap-2">
                <input
                  className="input-brox flex-1"
                  placeholder="sau adaugă URL poză..."
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
                  data-testid="admin-project-url-input"
                />
                <button type="button" onClick={addUrl} className="btn-ghost !px-4 !min-h-[48px] text-base" data-testid="admin-project-url-add">ADAUGĂ</button>
              </div>
            </div>
            {uploadErr && <p className="mt-3 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="admin-project-upload-error">{uploadErr}</p>}

            {form.images.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="admin-project-thumbs">
                {form.images.map((img, i) => (
                  <div key={`${img}-${i}`} className="relative">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cover: img })}
                      className={`block aspect-[4/3] w-full overflow-hidden border-2 ${form.cover === img ? "border-copper" : "border-transparent"}`}
                      data-testid={`admin-project-cover-${i}`}
                      title={form.cover === img ? "Imagine copertă" : "Setează ca copertă"}
                    >
                      <img src={imgSrc(img)} alt={`Poza ${i + 1}`} className="h-full w-full object-cover" />
                      {form.cover === img && (
                        <span className="absolute left-1 top-1 flex items-center gap-1 bg-copper px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-ink-950">
                          <Star className="h-3 w-3" /> Copertă
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center bg-ink-950/90 text-red-400 hover:text-red-300"
                      aria-label="Șterge poza"
                      data-testid={`admin-project-img-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-5">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-paper">
                <input type="checkbox" className="h-5 w-5 accent-[#D96B27]" checked={form.featured} onChange={set("featured")} data-testid="admin-project-featured-checkbox" />
                <Star className="h-4 w-4 text-copper" />
                Recomandat pe site
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-paper">
                <input type="checkbox" className="h-5 w-5 accent-[#D96B27]" checked={form.published} onChange={set("published")} data-testid="admin-project-published-checkbox" />
                Publicat
              </label>
              <label className="flex items-center gap-2 text-sm text-paper">
                Ordine
                <input type="number" className="input-brox !min-h-[44px] !w-20" value={form.order} onChange={set("order")} data-testid="admin-project-order-input" />
              </label>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setEditing(false)} className="btn-ghost !px-5 !py-2.5 !min-h-[44px] text-base" data-testid="admin-project-cancel-btn">ANULEAZĂ</button>
              <button type="submit" className="btn-primary !px-5 !py-2.5 !min-h-[44px] text-base" data-testid="admin-project-save-btn">SALVEAZĂ</button>
            </div>
          </div>
        </form>
      )}

      {projects === null && <p className="text-sm text-muted-foreground">Se încarcă...</p>}
      {projects !== null && sorted.length === 0 && (
        <p className="border border-dashed border-white/15 p-10 text-center text-sm text-muted-foreground" data-testid="admin-projects-empty">
          Niciun proiect adăugat. Adaugă proiecte reale, marchează-le „Recomandat" și publică-le.
        </p>
      )}

      <div className="space-y-3">
        {sorted.map((p) => (
          <div key={p._id} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-ink-900 px-5 py-4" data-testid={`admin-project-${p._id}`}>
            <div className="flex items-center gap-4">
              {p.cover && <img src={imgSrc(p.cover)} alt="" className="h-12 w-16 object-cover" />}
              <div>
                <p className="font-display text-lg font-bold uppercase text-paper">{p.title || p.category}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {p.category || "—"} · {p.images?.length || 0} poze · Ordine {p.order || 0} · {p.published ? "PUBLICAT" : "NEPUBLICAT"}
                  {p.featured && <span className="ml-2 text-copper">RECOMANDAT</span>}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEdit(p)} className="flex h-10 w-10 items-center justify-center border border-white/15 text-paper/70 hover:border-copper hover:text-copper" aria-label="Editează" data-testid={`admin-project-edit-${p._id}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(p._id)} className="flex h-10 w-10 items-center justify-center border border-white/15 text-paper/70 hover:border-red-500 hover:text-red-400" aria-label="Șterge" data-testid={`admin-project-delete-${p._id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
